import { queryOptions, useQuery } from '@tanstack/vue-query'

// Branding assets from /public/settings/design (public, read-only).
// The asset URL lives on `<field>.image.paths` (NOT on `value`, which is the ID).
// A `hero` thumbnail is requested for use as a large background image.
export const designSettingsQuery = queryOptions({
  queryKey: ['settings', 'design'],
  retry: 0,
  // Branding rarely changes — reuse SSR-dehydrated data, no client refetch.
  staleTime: 1000 * 60 * 30,
  // useApi().get returns a plain, serializable { data } and runs on the server too,
  // so the query can be dehydrated from SSR (settings are public — no auth needed).
  queryFn: async ({ signal }: { signal?: AbortSignal }) => {
    const { get } = useApi()
    return await get<DesignSettings>(
      '/public/settings/design',
      { thumb: { hero: { style: 'xl' }, ...OG_IMAGE_THUMB } },
      { signal }
    )
  },
  select: (res) => {
    const logoDoc = res?.data?.design_logo?.image
    const phDoc = res?.data?.design_general_img_placeholder?.image
    const productDoc = res?.data?.design_product_img_placeholder?.image
    const shareDoc = res?.data?.design_share_image?.image
    // Strip the force-download flag so the asset renders inline in <img>.
    const inline = (u?: string) => (u ? u.replace(/&dl=1\b/, '') : '')
    return {
      // SVG logo → use the original (download) path; fall back to a thumb.
      logo: inline(logoDoc?.paths?.download) || logoDoc?.paths?.thumb || '',
      generalPlaceholder:
        phDoc?.paths?.thumbs?.hero || phDoc?.paths?.download || phDoc?.paths?.thumb || '',
      productPlaceholder:
        productDoc?.paths?.thumbs?.hero || productDoc?.paths?.download || productDoc?.paths?.thumb || '',
      // Výchozí obrázek pro sdílení (og:image). Jen `og` thumb — originál může
      // být SVG, které sociální sítě nevykreslí, a několikamegový soubor navíc.
      shareImage: shareDoc?.paths?.thumbs?.og || '',
    }
  },
})

export function useDesignSettings() {
  return useQuery(designSettingsQuery)
}
